import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonWithLoading } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { BigInput } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { Link } from '#components/common/Link';
import { useDispatch } from '#redux';
import { loggedIn } from '#users/usersSlice';

export function getSaasErrorMessage(
  t: (key: string, options?: Record<string, unknown>) => string,
  error: string,
): string {
  switch (error) {
    case 'invalid-credentials':
      return t('Wrong email or password');
    case 'email-already-exists':
      return t('An account with this email already exists');
    case 'invalid-email':
      return t('Please enter a valid email address');
    case 'password-too-short':
      return t('Password must be at least 8 characters');
    case 'network-failure':
      return t('Unable to contact the server');
    default:
      return t('An unknown error occurred: {{error}}', { error });
  }
}

export function SaasLoginForm() {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const dispatch = useDispatch();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    if (email === '' || password === '' || loading) {
      return;
    }

    setError(null);
    setLoading(true);
    const result = await send('saas-sign-in', { email, password });
    setLoading(false);

    if ('error' in result) {
      setError(result.error);
    } else {
      void dispatch(loggedIn());
    }
  }

  return (
    <View style={{ marginTop: 5 }}>
      <View
        style={{
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <BigInput
          placeholder={t('Email')}
          type="email"
          onChangeValue={setEmail}
          onEnter={onSubmit}
        />
        <BigInput
          placeholder={t('Password')}
          type="password"
          onChangeValue={setPassword}
          onEnter={onSubmit}
        />
        <ButtonWithLoading
          variant="primary"
          isLoading={loading}
          style={{
            fontSize: 15,
            ...(isNarrowWidth ? { padding: 10 } : null),
          }}
          onPress={onSubmit}
        >
          <Trans>Sign in to Pesa Wise</Trans>
        </ButtonWithLoading>
      </View>

      <Text
        style={{
          fontSize: 14,
          color: theme.pageTextLight,
          marginTop: 12,
        }}
      >
        <Trans>
          New to Pesa Wise?{' '}
          <Link variant="internal" to="/signup">
            Create an account
          </Link>{' '}
          and get a free 30-day trial. Or{' '}
          <Link variant="internal" to="/reset-password">
            reset your password
          </Link>
          .
        </Trans>
      </Text>

      {error && (
        <Text
          style={{
            marginTop: 15,
            color: theme.errorText,
            fontSize: 15,
          }}
        >
          {getSaasErrorMessage(t, error)}
        </Text>
      )}
    </View>
  );
}
